import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import {
  ReturnRequestFilters,
  GetReturnRequestsQueryParams,
  ReturnRequestApiItem,
} from '../../../../pages/admin-dashboard/return-of-requests-list/return-of-requests-list.model';
import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { ReturnRequestsService } from '../../../../services/Return-Requests/return-requests';
import { LoadState } from '../../pharmacies/admin-pharmacies-list/admin-pharmacies-list.model';
import { ReturnRequestsFilterComponent } from './return-requests-filter/return-requests-filter';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { ActionInterface } from '../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { Loading } from '../../../../utils/loading/loading';

@Component({
  selector: 'app-list-of-returns-requests',
  standalone: true,
  imports: [CommonModule, ReturnRequestsFilterComponent, Tables, Pagination, Loading],
  templateUrl: './list-of-returns-requests.html',
  styleUrl: './list-of-returns-requests.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListOfReturnsRequests implements OnInit {
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private returnsService = inject(ReturnRequestsService);
  private pharmacyService = inject(PharmacyService);
  private destroyRef = inject(DestroyRef);

  isArabic = computed(() => String(this.locale).startsWith('ar'));

  state = signal<LoadState>('loading');
  rows = signal<Record<string, any>[]>([]);
  statusOptions = signal<{ id: string; titleEn: string; titleAr: string }[]>([]);

  currentPage = signal(1);
  itemsPerPage = signal(20);
  totalItems = signal(0);

  totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.itemsPerPage())));
  startItem = computed(() => (this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1));
  endItem = computed(() => Math.min(this.currentPage() * this.itemsPerPage(), this.totalItems()));

  filters = signal<ReturnRequestFilters>({
    orderId: '',
    pharmacyId: null,
    statusIds: [],
    createdOnFrom: null,
    createdOnTo: null,
    createdById: null,
    orderBy: 'LAST_MODIFICATION_DATE',
    orderDir: 'DESC',
  });

  isFiltersApplied = computed(() => {
    const f = this.filters();
    return !!(f.orderId || f.pharmacyId || f.statusIds.length || f.createdOnFrom || f.createdOnTo || f.createdById);
  });

  tableHeaderData = computed(() => [
    {
      key: 'returnRequestId',
      label: this.isArabic() ? 'رقم طلب المرتجع' : 'Return Request ID',
    },
    {
      key: 'orderId',
      label: this.isArabic() ? 'رقم الطلب' : 'Order ID',
      type: 'idHash',
    },
    {
      key: 'pharmacyOwnerName',
      label: this.isArabic() ? 'الصيدلية / المالك' : 'Pharmacy / Owner',
    },
    {
      key: 'returnValue',
      label: this.isArabic() ? 'قيمة المرتجع' : 'Return Value',
      type: 'currency',
    },
    {
      key: 'status',
      label: this.isArabic() ? 'حالة الطلب' : 'Status',
      type: 'status',
    },
    {
      key: 'createdOnBy',
      label: this.isArabic() ? 'تاريخ الانشاء / بواسطة' : 'Created On / By',
    },
    {
      key: 'actions',
      label: '',
      type: 'actions',
    },
  ]);

  currentActions = signal<ActionInterface[]>([
    {
      id: 1,
      titleAr: 'عرض التفاصيل',
      titleEn: 'View Details',
    } as ActionInterface,
  ]);

  private readonly RETURN_STATUS_DOMAIN_ID = 2603;

  ngOnInit(): void {
    this.loadStatuses();
    this.loadData();
  }

  loadStatuses(): void {
    this.pharmacyService
      .getAllStatuses(this.RETURN_STATUS_DOMAIN_ID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res?.list?.length) {
            const formatted = res.list.map((s: LookupVTO) => ({
              id: String(s.id),
              titleEn: s.titleEn,
              titleAr: s.titleAr ?? '',
            }));
            this.statusOptions.set(formatted);
          } else {
            this.setFallbackStatuses();
          }
        },
        error: () => this.setFallbackStatuses(),
      });
  }

  private setFallbackStatuses(): void {
    this.statusOptions.set([
      { id: '1', titleAr: 'قيد الانتظار', titleEn: 'Pending' },
      { id: '2', titleAr: 'مقبول', titleEn: 'Accepted' },
      { id: '3', titleAr: 'مرفوض', titleEn: 'Rejected' },
      { id: '4', titleAr: 'قيد المراجعة', titleEn: 'Under Review' },
      { id: '5', titleAr: 'منتهي الصلاحية', titleEn: 'Closed' },
    ]);
  }

  loadData(): void {
    this.state.set('loading');

    const f = this.filters();
    const params: GetReturnRequestsQueryParams = {
      orderId: f.orderId ? Number(f.orderId) : undefined,
      storeId: f.pharmacyId ?? undefined,
      requestStatusId: f.statusIds.length ? Number(f.statusIds[0]) : undefined,
      createdById: f.createdById ?? undefined,
      createdOnFrom: f.createdOnFrom || undefined,
      createdOnTo: f.createdOnTo || undefined,
      orderBy: f.orderBy || 'LAST_MODIFICATION_DATE',
      orderDir: f.orderDir || 'DESC',
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
    };

    this.returnsService
      .getReturnRequests(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          let rawList = res.list ?? [];
          if (f.orderId) {
            rawList = rawList.filter((item) => String(item.orderId).includes(f.orderId.trim()));
          }
          if (f.pharmacyId) {
            rawList = rawList.filter((item) => item.pharmacy?.id === f.pharmacyId);
          }
          if (f.statusIds.length) {
            rawList = rawList.filter((item) => f.statusIds.includes(String(item.status?.id)));
          }

          this.rows.set(rawList.map((item) => this.mapApiItemToTableRow(item)));
          this.totalItems.set(rawList.length ? (res.total ?? 0) : 0);
          this.state.set('success');
        },
        error: (err) => {
          console.error('Return Requests API Error:', err);
          this.rows.set([]);
          this.totalItems.set(0);
          this.state.set('error');
        },
      });
  }

  private mapApiItemToTableRow(item: ReturnRequestApiItem): Record<string, any> {
    const rawEn = (item.status?.titleEn || '').trim();
    const rawAr = (item.status?.titleAr || '').trim();
    const normalizedStatus = this.normalizeStatusForTable(rawEn, rawAr);

    return {
      id: item.id,
      returnRequestId: `RET-${String(item.id).padStart(6, '0')}`,
      orderId: item.orderId ?? null,
      pharmacyOwnerName: {
        pharmacyName: item.pharmacy?.nickName || '-',
        ownerName: item.pharmacy?.pharmacyOwner?.fullName || '-',
      },
      returnValue: item.returnValue ?? 0,
      status: {
        titleEn: normalizedStatus.titleEn,
        titleAr: normalizedStatus.titleAr,
      },
      createdOn: item.createdOn || null,
      createdBy: item.createdBy?.fullName || '-',
      lastModifiedOn: item.lastModifiedOn || null,
      lastModifiedBy: item.lastModifiedBy?.fullName || '-',
      isEditable: true,
    };
  }

  private normalizeStatusForTable(titleEn: string, titleAr: string): { titleEn: string; titleAr: string } {
    const en = titleEn.toLowerCase();
    const ar = titleAr;

    if (en.includes('pending') || ar.includes('انتظار')) {
      return { titleEn: 'Pending', titleAr: ar || 'قيد الانتظار' };
    }
    if (en.includes('accept') || ar.includes('مقبول')) {
      return { titleEn: 'Accepted', titleAr: ar || 'مقبول' };
    }
    if (en.includes('reject') || ar.includes('مرفوض')) {
      return { titleEn: 'Rejected', titleAr: ar || 'مرفوض' };
    }
    if (en.includes('review') || en.includes('modification') || ar.includes('مراجعة') || ar.includes('تعديل')) {
      return { titleEn: 'In Progress', titleAr: ar || 'قيد المراجعة' };
    }
    if (
      en.includes('expir') ||
      en.includes('closed') ||
      en.includes('cancel') ||
      ar.includes('منتهي') ||
      ar.includes('ملغي') ||
      ar.includes('مكتمل')
    ) {
      return { titleEn: 'Closed', titleAr: ar || 'منتهي الصلاحية' };
    }

    return {
      titleEn: titleEn || 'Pending',
      titleAr: titleAr || titleEn || '-',
    };
  }

  onApplyFilters(applied: ReturnRequestFilters): void {
    this.filters.set(applied);
    this.currentPage.set(1);
    this.loadData();
  }

  onResetFilters(): void {
    this.filters.set({
      orderId: '',
      pharmacyId: null,
      statusIds: [],
      createdOnFrom: null,
      createdOnTo: null,
      createdById: null,
      orderBy: 'LAST_MODIFICATION_DATE',
      orderDir: 'DESC',
    });
    this.currentPage.set(1);
    this.loadData();
  }

  getPageNumbers(): number[] {
    return Array.from({ length: this.totalPages() }, (_, i) => i + 1);
  }

  onPreviousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
      this.loadData();
    }
  }

  onNextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((p) => p + 1);
      this.loadData();
    }
  }

  onGoToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadData();
    }
  }

  onPageSizeChange(): void {
    this.currentPage.set(1);
    this.loadData();
  }

  onToggleDropdown(_event: unknown): void {}

  onActionTrigger(e: { event: Event; actionId: number; entity: any }): void {
    e.event?.stopPropagation?.();
    if (e.actionId === 1) {
      const id = e.entity?.id;
      if (id != null) {
        this.router.navigate(['/returns/returns-requests', id]);
      }
    }
  }
}
